import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import dayjs from '@/lib/dayjs'
import { useDateBackedOccasions } from '@/lib/services/occasions/occasions.query'
import { buildDateBackedOccasionOptions } from '@/lib/services/occasions/occasions.utils'
import { holidayOptions } from '@/lib/services/designs/designs.constants'
import { CalendarDays, Gift, Repeat } from 'lucide-react'

export const SCHEDULE_START_TYPE_FIXED_DATE = 'fixed_date'
export const SCHEDULE_START_TYPE_HOLIDAY_OFFSET = 'holiday_offset'
export const SCHEDULE_END_TYPE_NONE = 'none'
export const SCHEDULE_END_TYPE_FIXED_DATE = 'fixed_date'
export const SCHEDULE_END_TYPE_HOLIDAY_OFFSET = 'holiday_offset'

const selectorIconClass =
  'mt-0.5 shrink-0 rounded-xl border border-current/15 bg-background/80 p-2 shadow-sm'

const scheduleStartTypeOptions = [
  {
    id: SCHEDULE_START_TYPE_HOLIDAY_OFFSET,
    name: 'Holiday',
    start: (
      <span className={`${selectorIconClass} text-chart-1`} aria-hidden="true">
        <Gift className="size-5" />
      </span>
    ),
  },
  {
    id: SCHEDULE_START_TYPE_FIXED_DATE,
    name: 'Specific date',
    start: (
      <span className={`${selectorIconClass} text-chart-5`} aria-hidden="true">
        <CalendarDays className="size-5" />
      </span>
    ),
  },
]

const scheduleLabelIconClass = 'size-3.5 text-interaction'
const scheduleLabelWithIconClass = 'inline-flex items-center gap-1.5'

export const renderScheduleLabel = (label, Icon) => (
  <span className={scheduleLabelWithIconClass}>
    <span>{label}</span>
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label="Repeats yearly" className="inline-flex cursor-help" tabIndex={0}>
          <Icon className={scheduleLabelIconClass} aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent>Repeats yearly</TooltipContent>
    </Tooltip>
  </span>
)

export const toNonNegativeIntegerInput = (value) => String(value ?? '').replace(/\D/g, '')

export const scheduleFieldName = (fieldPrefix, field) =>
  fieldPrefix ? `${fieldPrefix}.${field}` : field

const getValueAtPath = (source, path) =>
  String(path ?? '')
    .split('.')
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source)

const getScheduleValuesFromState = (state, fieldPrefix) =>
  fieldPrefix ? getValueAtPath(state.values, fieldPrefix) : state.values

export const getScheduleFieldValue = (form, fieldPrefix, field) =>
  form.getFieldValue(scheduleFieldName(fieldPrefix, field))

const getScheduleFieldStateValue = (form, fieldPrefix, field) =>
  getValueAtPath(form.state?.values, scheduleFieldName(fieldPrefix, field))

export const setScheduleFieldValue = (form, fieldPrefix, field, value) =>
  form.setFieldValue(scheduleFieldName(fieldPrefix, field), value)

const setScheduleFieldValueIfChanged = (form, fieldPrefix, field, value) => {
  if (getScheduleFieldStateValue(form, fieldPrefix, field) !== value) {
    setScheduleFieldValue(form, fieldPrefix, field, value)
  }
}

export const switchScheduleToFixedDate = ({ form, fieldPrefix = 'schedule', todayDate }) => {
  const startsOn = getScheduleFieldStateValue(form, fieldPrefix, 'starts_on')
  const endsOn = getScheduleFieldStateValue(form, fieldPrefix, 'ends_on')

  if (!startsOn) {
    setScheduleFieldValue(form, fieldPrefix, 'starts_on', todayDate || dayjs().format('YYYY-MM-DD'))
  }

  setScheduleFieldValue(form, fieldPrefix, 'start_occasion_id', null)
  setScheduleFieldValue(form, fieldPrefix, 'end_occasion_id', null)
  setScheduleFieldValue(
    form,
    fieldPrefix,
    'end_type',
    endsOn ? SCHEDULE_END_TYPE_FIXED_DATE : SCHEDULE_END_TYPE_NONE
  )

  if (!endsOn) {
    setScheduleFieldValue(form, fieldPrefix, 'is_yearly', false)
  }
}

export const switchScheduleToHoliday = ({ form, fieldPrefix = 'schedule' }) => {
  const startHoliday = getScheduleFieldStateValue(form, fieldPrefix, 'start_holiday')
  const endOffsetDays = getScheduleFieldStateValue(form, fieldPrefix, 'end_offset_days')

  setScheduleFieldValue(form, fieldPrefix, 'is_yearly', false)
  setScheduleFieldValue(form, fieldPrefix, 'end_type', SCHEDULE_END_TYPE_HOLIDAY_OFFSET)
  setScheduleFieldValue(form, fieldPrefix, 'end_matches_start_holiday', true)

  if (endOffsetDays === null || endOffsetDays === undefined || endOffsetDays === '') {
    setScheduleFieldValue(form, fieldPrefix, 'end_offset_days', 7)
  }

  if (startHoliday) {
    setScheduleFieldValue(form, fieldPrefix, 'end_holiday', startHoliday)
  }
}

export const handleStartHolidayScheduleChange = ({ form, fieldPrefix = 'schedule', value }) => {
  if (value) {
    setScheduleFieldValue(form, fieldPrefix, 'end_matches_start_holiday', true)
    setScheduleFieldValue(form, fieldPrefix, 'end_holiday', value)
  }
}

export const handleFixedEndDateScheduleChange = ({ form, fieldPrefix = 'schedule', value }) => {
  setScheduleFieldValueIfChanged(
    form,
    fieldPrefix,
    'end_type',
    value ? SCHEDULE_END_TYPE_FIXED_DATE : SCHEDULE_END_TYPE_NONE
  )

  if (!value) {
    setScheduleFieldValueIfChanged(form, fieldPrefix, 'is_yearly', false)
  }
}

export const clearScheduleFixedEndDate = ({ form, fieldPrefix = 'schedule' }) => {
  setScheduleFieldValue(form, fieldPrefix, 'ends_on', '')
  setScheduleFieldValue(form, fieldPrefix, 'end_type', SCHEDULE_END_TYPE_NONE)
  setScheduleFieldValue(form, fieldPrefix, 'is_yearly', false)
}

export const getScheduleEndDateMin = ({
  initialData,
  startType,
  startsOn,
  isYearly,
  createMinDate,
}) => {
  if (startType === SCHEDULE_START_TYPE_FIXED_DATE && startsOn && !isYearly) {
    return dayjs(startsOn)
  }

  return initialData ? null : createMinDate
}

const noop = () => {}
const defaultScheduleChangeHandler = (listener) => (payload) => listener?.(payload)

export function ScheduleAutomationFields({
  clearFixedEndDate = noop,
  fieldPrefix = 'schedule',
  fixedEndHelpText = 'If no end date is set, this schedule will remain active indefinitely.',
  form,
  handleFixedEndDateChange = noop,
  handleScheduleFieldChange = defaultScheduleChangeHandler,
  handleStartHolidayChange = noop,
  id,
  initialData = null,
  renderExtraFields = null,
  scheduleMaxDate,
  scheduleMinDate,
  scheduleTypeLabel = 'Choose a holiday-based schedule or a specific date range.',
  scheduleTypeWrapperClassName = '',
  switchToFixedDateSchedule = noop,
  switchToHolidaySchedule = noop,
}) {
  const { data: dateBackedOccasions = [] } = useDateBackedOccasions()
  const occasionOptions = buildDateBackedOccasionOptions({
    occasions: dateBackedOccasions,
    fallbackOptions: holidayOptions,
  })
  const occasionIdForHoliday = (holidayKey) =>
    occasionOptions.find((option) => option.id === holidayKey)?.occasionId ?? null
  const fieldName = (field) => scheduleFieldName(fieldPrefix, field)

  return (
    <div className="@container/schedule-fields space-y-5">
      <div className={scheduleTypeWrapperClassName}>
        <form.AppField
          name={fieldName('start_type')}
          listeners={{
            onChange: handleScheduleFieldChange(({ value }) => {
              if (value === SCHEDULE_START_TYPE_FIXED_DATE) {
                switchToFixedDateSchedule()
              }

              if (value === SCHEDULE_START_TYPE_HOLIDAY_OFFSET) {
                switchToHolidaySchedule()
                const startHoliday = getScheduleFieldStateValue(form, fieldPrefix, 'start_holiday')

                if (startHoliday) {
                  const startOccasionId = occasionIdForHoliday(startHoliday)
                  setScheduleFieldValueIfChanged(
                    form,
                    fieldPrefix,
                    'start_occasion_id',
                    startOccasionId
                  )

                  if (
                    getScheduleFieldStateValue(form, fieldPrefix, 'end_type') ===
                      SCHEDULE_END_TYPE_HOLIDAY_OFFSET &&
                    getScheduleFieldStateValue(form, fieldPrefix, 'end_matches_start_holiday') !==
                      false
                  ) {
                    setScheduleFieldValueIfChanged(
                      form,
                      fieldPrefix,
                      'end_occasion_id',
                      startOccasionId
                    )
                  }
                }
              }
            }),
          }}
        >
          {(field) => (
            <field.RadioButtonGroup
              appearance="selectorCard"
              id={`${id}-schedule-type`}
              label={scheduleTypeLabel}
              className="@container/schedule-type w-full"
              aria-label="Schedule type"
              options={scheduleStartTypeOptions}
              size="sm"
              joined={false}
              unselectedVariant="outline"
              classNames={{
                container: 'grid w-full gap-3 @[34rem]/schedule-type:grid-cols-2',
              }}
            />
          )}
        </form.AppField>
      </div>

      <form.Subscribe
        selector={(state) => getScheduleValuesFromState(state, fieldPrefix)?.start_type}
      >
        {(startType) => (
          <div className="space-y-5">
            {renderExtraFields?.({ startType })}

            {startType === SCHEDULE_START_TYPE_HOLIDAY_OFFSET ? (
              <HolidayScheduleFields
                fieldName={fieldName}
                form={form}
                handleScheduleFieldChange={handleScheduleFieldChange}
                handleStartHolidayChange={handleStartHolidayChange}
                id={id}
                occasionIdForHoliday={occasionIdForHoliday}
                occasionOptions={occasionOptions}
                fieldPrefix={fieldPrefix}
              />
            ) : (
              <FixedDateScheduleFields
                clearFixedEndDate={clearFixedEndDate}
                fieldName={fieldName}
                fixedEndHelpText={fixedEndHelpText}
                form={form}
                handleFixedEndDateChange={handleFixedEndDateChange}
                handleScheduleFieldChange={handleScheduleFieldChange}
                id={id}
                initialData={initialData}
                scheduleMaxDate={scheduleMaxDate}
                scheduleMinDate={scheduleMinDate}
                startType={startType}
                fieldPrefix={fieldPrefix}
              />
            )}
          </div>
        )}
      </form.Subscribe>
    </div>
  )
}

function HolidayScheduleFields({
  fieldName,
  form,
  handleScheduleFieldChange,
  handleStartHolidayChange,
  id,
  occasionIdForHoliday,
  occasionOptions,
  fieldPrefix,
}) {
  return (
    <div className="grid gap-4 @[34rem]/schedule-fields:grid-cols-4">
      <form.AppField name={fieldName('start_holiday')}>
        {(field) => (
          <field.SelectInput
            id={`${id}-start-holiday`}
            label={renderScheduleLabel('Holiday', Repeat)}
            ariaLabel="Holiday"
            options={occasionOptions}
            placeholder="Choose holiday"
            className="@[34rem]/schedule-fields:col-span-2"
            onValueChange={(value) => {
              handleScheduleFieldChange(() => {
                const startOccasionId = occasionIdForHoliday(value)

                setScheduleFieldValueIfChanged(
                  form,
                  fieldPrefix,
                  'start_occasion_id',
                  startOccasionId
                )
                handleStartHolidayChange(value)

                if (
                  getScheduleFieldStateValue(form, fieldPrefix, 'end_type') ===
                  SCHEDULE_END_TYPE_HOLIDAY_OFFSET
                ) {
                  setScheduleFieldValueIfChanged(
                    form,
                    fieldPrefix,
                    'end_occasion_id',
                    startOccasionId
                  )
                  setScheduleFieldValueIfChanged(
                    form,
                    fieldPrefix,
                    'end_matches_start_holiday',
                    true
                  )
                }
              })({ value })
            }}
          />
        )}
      </form.AppField>

      <form.AppField
        name={fieldName('start_offset_days')}
        listeners={{
          onChange: handleScheduleFieldChange(),
        }}
      >
        {(field) => (
          <field.TextInput
            id={`${id}-start-offset-days`}
            type="text"
            inputMode="numeric"
            label="Days Before Holiday"
            placeholder="Example: 21"
            transformValue={toNonNegativeIntegerInput}
          />
        )}
      </form.AppField>

      <form.AppField
        name={fieldName('end_offset_days')}
        listeners={{
          onChange: handleScheduleFieldChange(),
        }}
      >
        {(field) => (
          <field.TextInput
            id={`${id}-end-offset-days`}
            type="text"
            inputMode="numeric"
            label="Days After Holiday"
            placeholder="Example: 7"
            transformValue={toNonNegativeIntegerInput}
          />
        )}
      </form.AppField>
    </div>
  )
}

function FixedDateScheduleFields({
  clearFixedEndDate,
  fieldName,
  fixedEndHelpText,
  form,
  handleFixedEndDateChange,
  handleScheduleFieldChange,
  id,
  initialData,
  scheduleMaxDate,
  scheduleMinDate,
  startType,
  fieldPrefix,
}) {
  return (
    <form.Subscribe
      selector={(state) => {
        const schedule = getScheduleValuesFromState(state, fieldPrefix)

        return {
          end_type: schedule?.end_type,
          ends_on: schedule?.ends_on,
          is_yearly: schedule?.is_yearly,
          starts_on: schedule?.starts_on,
        }
      }}
    >
      {({ end_type: endType, ends_on: endsOn, is_yearly: isYearly, starts_on: startsOn }) => (
        <div className="space-y-4">
          <div className="grid gap-4 @[34rem]/schedule-fields:grid-cols-2">
            <form.AppField
              name={fieldName('starts_on')}
              listeners={{
                onChange: handleScheduleFieldChange(),
              }}
            >
              {(field) => (
                <field.DatePicker
                  id={`${id}-starts-on`}
                  label="Starts On"
                  className="w-full"
                  format="MMM D, YYYY"
                  minDate={scheduleMinDate}
                  maxDate={scheduleMaxDate}
                />
              )}
            </form.AppField>

            <div className="space-y-2">
              <form.AppField
                name={fieldName('ends_on')}
                listeners={{
                  onChange: handleScheduleFieldChange(({ value }) => {
                    handleFixedEndDateChange(value)
                  }),
                }}
              >
                {(field) => (
                  <field.DatePicker
                    id={`${id}-ends-on`}
                    label="Ends On"
                    placeholder="No end date"
                    className="w-full"
                    format="MMM D, YYYY"
                    minDate={getScheduleEndDateMin({
                      initialData,
                      startType,
                      startsOn,
                      isYearly,
                      createMinDate: scheduleMinDate,
                    })}
                    maxDate={scheduleMaxDate}
                  />
                )}
              </form.AppField>

              {endsOn ? (
                <Button type="button" variant="linkMuted" size="inline" onClick={clearFixedEndDate}>
                  Clear end date
                </Button>
              ) : (
                <Typography variant="supporting" className="block">
                  {fixedEndHelpText}
                </Typography>
              )}
            </div>
          </div>

          {endType === SCHEDULE_END_TYPE_FIXED_DATE && endsOn ? (
            <form.AppField
              name={fieldName('is_yearly')}
              listeners={{
                onChange: handleScheduleFieldChange(),
              }}
            >
              {(field) => (
                <field.SwitchInput
                  label={renderScheduleLabel('Repeat yearly', Repeat)}
                  description="Reuse this fixed-date schedule every year."
                />
              )}
            </form.AppField>
          ) : null}
        </div>
      )}
    </form.Subscribe>
  )
}
